// src/components/UiFeedbackWidget/submit.js
//
// Spec 20260925-ui-feedback §6.1 / §6.2 / §9.1, plan T1.29. Tasks T047.
//
// Building the context and sending it. The ONE network call of the whole widget is
// ideasApi.create — no AI, no telemetry, nothing else (AC-11, UD-NO-AI-INLINE).
//
// The context is built AT SEND TIME from the current page — unless an element was picked,
// in which case it is pinned to the page of that element (spec §6.2). Only keys the server
// accepts leave the browser (UI_CONTEXT_KEYS); the server-only flags never do, and long
// values are clipped to the server's limits here so a long nav label cannot 422 a remark.

import { UI_CONTEXT_KEYS, UI_CONTEXT_LIMITS, PORTAL_LOCALES } from '@caro/shared/ideas';

import { ideasApi } from '../../services/apiClient';
import { APP_VERSION, APP_COMMIT } from '../../utils/appVersion';

const clip = (v, max) => (typeof v === 'string' && v ? v.slice(0, max) : null);

/** 'nl-BE' → 'nl'; anything that is not one of the six portal languages → null. */
export function normaliseLocale(lang) {
  if (typeof lang !== 'string' || !lang) return null;
  const l = lang.toLowerCase();
  if (PORTAL_LOCALES.includes(l)) return l;
  const base = l.split(/[-_]/)[0];
  return PORTAL_LOCALES.includes(base) ? base : null;
}

export function currentAppVersion() {
  const v = APP_COMMIT && APP_COMMIT !== 'dev' ? `${APP_VERSION}+${APP_COMMIT}` : APP_VERSION;
  return clip(v, UI_CONTEXT_LIMITS.appVersion);
}

/**
 * Pure. Only UI_CONTEXT_KEYS, null/empty values omitted.
 *
 * @param {{ route, pageId, pageLabel, tab }} page
 * @param {{ elementKbId, elementPath, elementRole, elementLabel }|null} element
 * @param {string[]} categories
 * @param {{ viewport?, colorMode?, locale?, appVersion?, pinned? }} env
 */
export function buildUiContext(page, element, categories, env = {}) {
  const ctx = {
    route: page && page.route,
    pageId: page && page.pageId,
    pageLabel: clip(page && page.pageLabel, UI_CONTEXT_LIMITS.pageLabel),
    tab: clip(page && page.tab, UI_CONTEXT_LIMITS.tab),
    categories: Array.isArray(categories) && categories.length ? [...new Set(categories)] : null,
    elementKbId: (element && element.elementKbId) || null,
    elementPath:
      element && Array.isArray(element.elementPath) && element.elementPath.length
        ? element.elementPath.slice(-UI_CONTEXT_LIMITS.elementPath)
        : null,
    elementRole: clip(element && element.elementRole, UI_CONTEXT_LIMITS.elementRole),
    elementLabel: clip(element && element.elementLabel, UI_CONTEXT_LIMITS.elementLabel),
    viewport: env.viewport || null,
    colorMode: env.colorMode === 'dark' || env.colorMode === 'light' ? env.colorMode : null,
    locale: normaliseLocale(env.locale),
    appVersion: clip(env.appVersion, UI_CONTEXT_LIMITS.appVersion),
    pinned: typeof env.pinned === 'boolean' ? env.pinned : null,
  };
  const out = {};
  for (const key of UI_CONTEXT_KEYS) {
    const v = ctx[key];
    if (v === null || v === undefined || v === '') continue;
    out[key] = v;
  }
  return out;
}

export function currentViewport() {
  const clamp = (n) => Math.min(Math.max(Math.round(Number(n) || 1), 1), 10000);
  return { w: clamp(window.innerWidth), h: clamp(window.innerHeight) };
}

/** POST /api/ideas { kind: 'ui', body, uiContext } — no title (the server derives it). */
export async function submitUiFeedback({ body, uiContext }) {
  const res = await ideasApi.create({ kind: 'ui', body, uiContext });
  return (res && res.data && res.data.data) || null;
}

/**
 * Map an API error onto what the panel says.
 * @returns {{ kind: 'rate_limited'|'forbidden'|'invalid'|'generic', limit?: number, field?: string }}
 */
export function classifyError(err) {
  const status = err && err.response && err.response.status;
  const data = (err && err.response && err.response.data) || {};
  if (status === 429 || data.code === 'IDEA_RATE_LIMIT_USER' || data.code === 'IDEA_RATE_LIMIT_COMPANY') {
    const headers = (err.response && err.response.headers) || {};
    const limit = Number(headers['ratelimit-limit'] || headers['RateLimit-Limit']);
    return { kind: 'rate_limited', limit: Number.isFinite(limit) && limit > 0 ? limit : undefined };
  }
  if (status === 403) return { kind: 'forbidden' };
  if (status === 422) {
    const field = data.details && data.details.field;
    return { kind: 'invalid', field };
  }
  return { kind: 'generic' };
}
